import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { ApiResponse, ContactNoteDto } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { contactNotesQueryKey } from '@/hooks/queries/use-contact-notes-query';

interface DeleteContactNoteContext {
  previous?: ContactNoteDto[];
}

const deleteContactNote = async (creatorIdentityId: string, noteId: string): Promise<void> => {
  await api.delete(`/dashboard/contacts/${creatorIdentityId}/notes/${noteId}`);
};

/**
 * Removes a note, optimistically dropping it from the cached list. Mirrors the
 * other note mutations: cancel + snapshot + setQueryData, rollback on error,
 * invalidate on settled.
 */
export const useDeleteContactNote = (creatorIdentityId: string | null) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = contactNotesQueryKey(creatorIdentityId);

  return useMutation<void, AxiosError<ApiResponse>, string, DeleteContactNoteContext>({
    mutationFn: (noteId) => deleteContactNote(creatorIdentityId!, noteId),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ContactNoteDto[]>(queryKey);

      queryClient.setQueryData<ContactNoteDto[]>(queryKey, (current) =>
        (current ?? []).filter((note) => note.id !== noteId)
      );

      return { previous };
    },
    onError: (error, _noteId, context) => {
      queryClient.setQueryData(queryKey, context?.previous);

      toaster.error({
        title: t('contactDrawer.notes.deleteError'),
        description: error.response?.data?.message || t('common.errors.unexpected'),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
